import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ExtendIcon,
  Visibility as ViewIcon
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import * as iotAPI from '../../data/iot'
import { IoTDeviceRegistrationModal } from './IoTDeviceRegistrationModal'
import { IoTActivationCodeModal } from './IoTActivationCodeModal'
import { IoTDeviceDeleteModal } from './IoTDeviceDeleteModal'
import { IoTTokenExtensionModal } from './IoTTokenExtensionModal'
import { IoTDeviceEditModal } from './IoTDeviceEditModal'

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 650
  },
  statusChip: {
    minWidth: 80
  },
  actionsCell: {
    width: 120
  }
}))

export const IoTDevicesPanel = ({ id }) => {
  const classes = useStyles()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false)
  const [activationCodeModalOpen, setActivationCodeModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [tokenExtensionModalOpen, setTokenExtensionModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState(null)

  const fetchDevices = async () => {      
    setLoading(true)
    setError(null)
    
    try {
      const deviceList = await iotAPI.getIoTDevices(id)
      setDevices(deviceList || [])
    } catch (err) {
      console.error('Error fetching IoT devices:', err)
      setError('Failed to fetch IoT devices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      fetchDevices()
    }
  }, [id])

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'primary'
      case 'inactive': return 'default'
      case 'expired': return 'secondary'
      default: return 'default'
    }
  }

  const getStatusLabel = (device) => {
    if (device.activatedAt) {
      return 'Active'
    }
    return 'Inactive'
  }

  const handleAddDevice = () => {
    setRegistrationModalOpen(true)
  }

  const handleDeviceRegistered = () => {
    // Refresh the device list after successful registration
    fetchDevices()
  }

  const handleEditDevice = (device) => {
    setSelectedDevice(device)
    setEditModalOpen(true)
  }

  const handleDeviceUpdated = () => {
    // Refresh the device list after successful update
    fetchDevices()
  }

  const handleDeleteDevice = (device) => {
    setSelectedDevice(device)
    setDeleteModalOpen(true)
  }

  const handleDeviceDeleted = () => {
    // Refresh the device list after successful deletion
    fetchDevices()
  }

  const handleExtendToken = (device) => {
    setSelectedDevice(device)
    setTokenExtensionModalOpen(true)
  }

  const handleTokenExtended = () => {
    // Refresh the device list after successful token extension
    fetchDevices()
  }

  const handleViewActivationCode = (device) => {
    setSelectedDevice(device)
    setActivationCodeModalOpen(true)
  }

  if (!id) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          No modality selected
        </Alert>
      </Box>
    )
  }

  return (
    <Box p={3}>
      <div className={classes.header}>
        <Typography variant="h6">
          IoT Devices
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddDevice}
        >
          Add IoT Device
        </Button>
      </div>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label="IoT devices table">
          <TableHead>
            <TableRow>
              <TableCell>Device ID</TableCell>
              <TableCell>Device Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Registration Date</TableCell>
              <TableCell>Last Seen</TableCell>
              <TableCell>Token Status</TableCell>
              <TableCell className={classes.actionsCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No IoT devices found for this modality
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <TableRow key={device.id}>
                  <TableCell>{device.deviceId}</TableCell>
                  <TableCell>{device.deviceType}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(device)}
                      color={getStatusColor(device.activatedAt ? 'active' : 'inactive')}
                      size="small"
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell>{device.location || 'N/A'}</TableCell>
                  <TableCell>
                    {device.createdAt 
                      ? new Date(device.createdAt).toLocaleDateString()
                      : 'N/A'
                    }
                  </TableCell>
                  <TableCell>
                    {device.lastSeenAt 
                      ? new Date(device.lastSeenAt).toLocaleDateString()
                      : 'Never'
                    }
                  </TableCell>
                  <TableCell>
                    {device.activatedAt ? 'Valid' : 'Not Activated'}
                  </TableCell>
                  <TableCell className={classes.actionsCell}>
                    <Tooltip title="Edit Device">
                      <IconButton
                        size="small"
                        onClick={() => handleEditDevice(device)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {!device.activatedAt ? (
                      <Tooltip title="View Activation Code">
                        <IconButton
                          size="small"
                          onClick={() => handleViewActivationCode(device)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Extend Token">
                        <IconButton
                          size="small"
                          onClick={() => handleExtendToken(device)}
                        >
                          <ExtendIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete Device">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDevice(device)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <IoTDeviceRegistrationModal
        open={registrationModalOpen}
        onClose={() => setRegistrationModalOpen(false)}
        modalityId={id}
        onDeviceRegistered={handleDeviceRegistered}
      />

      <IoTActivationCodeModal
        open={activationCodeModalOpen}
        onClose={() => setActivationCodeModalOpen(false)}
        device={selectedDevice}
        modalityId={id}
      />

      <IoTDeviceDeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        device={selectedDevice}
        modalityId={id}
        onDeviceDeleted={handleDeviceDeleted}
      />

      <IoTTokenExtensionModal
        open={tokenExtensionModalOpen}
        onClose={() => setTokenExtensionModalOpen(false)}
        device={selectedDevice}
        modalityId={id}
        onTokenExtended={handleTokenExtended}
      />

      <IoTDeviceEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        device={selectedDevice}
        modalityId={id}
        onDeviceUpdated={handleDeviceUpdated}
      />
    </Box>
  )
}

